import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { socketManager } from "../helpers/socket";
import { isAuthenticated } from "../helpers/auth";

const SocketContext = createContext({
  isConnected: false,
  isAuthenticated: false,
  socketId: null,
  onAppointmentBooked: () => () => {},
  onAppointmentUpcoming: () => () => {},
});

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within SocketProvider");
  }
  return context;
};

const AUTH_CHANGED_EVENT = "provider-auth-changed";

export const SocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isSocketAuthenticated, setIsSocketAuthenticated] = useState(false);
  const [socketId, setSocketId] = useState(null);
  const cleanupRef = useRef([]);

  const updateState = useCallback(() => {
    setIsConnected(socketManager.isSocketConnected());
    setIsSocketAuthenticated(socketManager.isSocketAuthenticated());
    setSocketId(socketManager.getSocketId());
  }, []);

  const handleAuthChange = useCallback(() => {
    if (isAuthenticated()) {
      socketManager.reconnect();
    } else {
      socketManager.fullDisconnect();
    }
    updateState();
  }, [updateState]);

  useEffect(() => {
    const cleanupAuthState = socketManager.onAuthStateChange(updateState);
    const onAuthChanged = () => handleAuthChange();

    window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    cleanupRef.current = [cleanupAuthState];

    if (isAuthenticated()) {
      socketManager.connect();
    }
    updateState();

    return () => {
      cleanupRef.current.forEach((cleanup) => cleanup());
      cleanupRef.current = [];
      window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    };
  }, [handleAuthChange, updateState]);

  const onAppointmentBooked = useCallback(
    (callback) => socketManager.onAppointmentBooked(callback),
    []
  );

  const onAppointmentUpcoming = useCallback(
    (callback) => socketManager.onAppointmentUpcoming(callback),
    []
  );

  const value = useMemo(
    () => ({
      isConnected,
      isAuthenticated: isSocketAuthenticated,
      socketId,
      onAppointmentBooked,
      onAppointmentUpcoming,
    }),
    [
      isConnected,
      isSocketAuthenticated,
      socketId,
      onAppointmentBooked,
      onAppointmentUpcoming,
    ]
  );

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};
